/**
 * Matches any artifact tag a user can produce or mention in the chat input:
 *   [[workflow: darwinName|Display Name]]
 *   [[dataframe: table_name]]
 *   [[chart: chart_name]]
 *   [[code: snippet_name]]
 *   [[report: report_name]]
 *
 * These tags are semantic markers sent to the agent as-is.
 * In the editor they are rendered as styled non-editable chips (MessageInput).
 * In sent messages they are rendered as clickable ArtifactLink chips (Message).
 *
 * --- Workflow tag format note ---
 * Workflow tags use "darwinName|Display Name" as the body instead of just the
 * darwin name. This is because:
 *   - The darwin name is the machine key used at runtime to look up the artifact.
 *   - The display name (header.name) is the human-readable label shown in chips.
 *   - Since buildNodes (MessageInput) has no access to the artifact map at parse
 *     time, encoding both in the tag body is the only way to show the correct
 *     label both during input and when re-rendering a saved message from the DB.
 * Consumers MUST split on "|" to extract the darwin name before using it for
 * lookups (see Message.tsx and MessageInput.tsx).
 *
 * Group 1 → tag type  (workflow | dataframe | chart | code | report)
 * Group 2 → tag body  (everything between the type colon and closing ]])
 */
export const ARTIFACT_TAG_TYPE = {
  workflow: "workflow",
  workflowRun: "workflow-run",
  dataframe: "dataframe",
  chart: "chart",
  code: "code",
  report: "report",
} as const;

export const ARTIFACT_TAG_RE = /\[\[(workflow|dataframe|chart|code|report):\s*([^\]]+)\]\]/gi;

/**
 * Build a canonical workflow tag string.
 *
 * @param lookupKey  The darwin_name (or header.id as fallback) used by the agent and
 *                   findWorkflowArtifactByDarwinName for runtime resolution.
 * @param displayName  The human-readable label shown in chips. When omitted the tag
 *                     uses the simple single-part format used by agent-generated tags.
 *
 * Examples:
 *   buildWorkflowTag("daily_cpl_brazil")
 *     → "[[workflow: daily_cpl_brazil]]"
 *   buildWorkflowTag("daily_cpl_brazil", "Daily CPL — Brazil")
 *     → "[[workflow: daily_cpl_brazil|Daily CPL — Brazil]]"
 */
export function buildWorkflowTag(lookupKey: string, displayName?: string): string {
  const key = lookupKey.trim();
  const name = displayName?.trim();
  return name ? `[[${ARTIFACT_TAG_TYPE.workflow}: ${key}|${name}]]` : `[[${ARTIFACT_TAG_TYPE.workflow}: ${key}]]`;
}

/**
 * Strip the |displayName suffix from workflow tags before the message is sent
 * to the AI. The display name is purely a frontend rendering aid and should not
 * be included in the text that reaches Darwin.
 *
 * Priority: left part (darwin_name / lookup key) if non-empty; otherwise fall
 * back to the right part (display name) so something meaningful still reaches Darwin.
 *
 *   "[[workflow: daily_cpl|Daily CPL — Brazil]]"  →  "[[workflow: daily_cpl]]"
 *   "[[workflow: |Daily CPL — Brazil]]"            →  "[[workflow: Daily CPL — Brazil]]"
 *   "[[workflow: daily_cpl]]"                      →  unchanged
 */
export function normalizeWorkflowTagsForSend(message: string): string {
  return message.replace(/\[\[workflow:\s*([^|\]]*)\|([^\]]*)\]\]/gi, (_, key, displayName) => {
    const k = key.trim();
    const d = displayName.trim();
    return `[[${ARTIFACT_TAG_TYPE.workflow}: ${k || d}]]`;
  });
}

export type MatchResult = { matched: boolean; score: number; ranges: [number, number][] };

/**
 * Check whether a search query matches a label using a single-pass subsequence scan.
 *
 * Every character in the query must appear in the label in order, but gaps are allowed —
 * this is a strict superset of substring matching. Examples:
 *   "channel"    → "analyze_channel_performance"  ✓ (contiguous run)
 *   "channelperf"→ "analyze_channel_performance"  ✓ (non-contiguous subsequence)
 *   "brazilcpl"  → "brazil_leads_cpl_last_week"   ✓ (characters span multiple tokens)
 *   "bgc"        → "background_capacity"          ✓ (b=background[0], g=background[4], c=capacity[0])
 *
 * Scoring rewards two things so that better matches sort above weaker ones:
 *   - Consecutive matched characters  (+2 per character in the run, compounding)
 *   - Matches that land on a word boundary  (+5 per hit)
 *
 * Word boundaries are detected at: the start of the string, after any _ or space
 * separator, and at camelCase transitions (lowercase → uppercase).
 *
 * The returned `ranges` are contiguous [start, end] index pairs (inclusive, on the
 * lowercased label) of matched character runs — useful for highlighting in the UI.
 */
export function labelMatchesQuery(label: string, query: string): MatchResult {
  if (!query) return { matched: true, score: 0, ranges: [] };

  const l = label.toLowerCase();
  const q = query.toLowerCase();

  // Build the set of word-boundary positions.
  const boundaries = new Set<number>();
  boundaries.add(0);
  for (let i = 1; i < l.length; i++) {
    // After a separator (underscore or space).
    if (/[_\s]/.test(l[i - 1])) boundaries.add(i);
    // camelCase transition: previous char is lowercase, current char in the original
    // label is uppercase (check `label[i]`, not `l[i]`, to preserve case information).
    if (/[a-z]/.test(l[i - 1]) && /[A-Z]/.test(label[i])) boundaries.add(i);
  }

  let qi = 0;
  let score = 0;
  let consecutive = 0;
  const ranges: [number, number][] = [];
  let rangeStart = -1;

  for (let li = 0; li < l.length && qi < q.length; li++) {
    if (l[li] === q[qi]) {
      if (rangeStart === -1) rangeStart = li;
      consecutive++;
      score += consecutive * 2; // compounding bonus for contiguous runs
      if (boundaries.has(li)) score += 5; // extra reward for hitting a word boundary
      qi++;
    } else {
      if (rangeStart !== -1) {
        ranges.push([rangeStart, li - 1]);
        rangeStart = -1;
      }
      consecutive = 0;
    }
  }

  // Close any open range that reaches the end of the label.
  if (rangeStart !== -1) ranges.push([rangeStart, l.length - 1]);

  return { matched: qi === q.length, score, ranges };
}
